import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { FeatureDispatch } from 'features/assetViz/objects/features/FeatureDispatch'
import {
  cartesianCircleGeometry,
  sphereGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoAssetModel, GeckoHemisphericalHead } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a hemispherical head - a dome-shaped end cap
 * for pressure vessels. Similar to a dome but designed specifically
 * for pressure vessel applications.
 *
 * @param model       The hemispherical head component model
 * @param assetModel  The asset model containing this head
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const HemisphericalHeadSurface3D: React.FC<
  {
    model: GeckoHemisphericalHead
    assetModel?: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, plates } = React.useMemo(() => {
    const bounds = model.componentBounds

    // Hemispherical heads are spherical surfaces - use same scaling as domes
    const scale = extractScale(groupProps.scale).multiply(
      new Vector3(model.radius.num, model.radius.num, model.radius.num)
    )

    return {
      bounds,
      scale,
      plates: model.platesArray,
    }
  }, [model, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return cartesianCircleGeometry
    return sphereGeometry
  }, [show3D])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      <FeatureDispatch features={plates} />
    </ModelSurface3D>
  )
}
